import React, { useState, useEffect } from "react";
import { GridStack } from "gridstack";
import "gridstack/dist/gridstack.min.css";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    AreaChart,
    Area,
} from "recharts";
import { Eye, Activity, TrendingUp, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "../style/DashboardPage.css";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";

const DashboardPage = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [sensores, setSensores] = useState([]);
    const [datosAgrupados, setDatosAgrupados] = useState({});
    const [promedios, setPromedios] = useState({});
    const [ultimosValores, setUltimosValores] = useState({});
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        cargarDatosDashboard();
        const interval = setInterval(cargarDatosDashboard, 30000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (!loading) {
            GridStack.init({
                column: 12,
                cellHeight: 100,
                margin: 10,
                float: false,
                resizable: {
                    handles: "e, se, s, sw, w",
                    autoHide: false
                },
                draggable: {
                    handle: ".paella-titulo, .sushi-label",
                    scroll: true
                },
                disableOneColumnMode: true,
                animate: true,

                acceptWidgets: true,
                removable: false,
                removeTimeout: 2000,

                compact: 'compact',
                minRow: 1,
            });
        }
    }, [loading]);

    const cargarDatosDashboard = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const resSensores = await fetch("https://agro-nexus-prod.vercel.app/api/sensores", {
                headers: { Authorization: `Bearer ${token}` }
            });
            const dataSensores = await resSensores.json();

            if (dataSensores.success) {
                setSensores(dataSensores.sensores);

                const datos = {};
                const promediosTemp = {};
                const ultimosTemp = {};

                for (const sensor of dataSensores.sensores) {
                    const resDatos = await fetch(
                        `https://agro-nexus-prod.vercel.app/api/sensores/${sensor.id}/datos?limite=20`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    const dataDatos = await resDatos.json();

                    if (dataDatos.success && dataDatos.datos.length > 0) {
                        const datosFormateados = dataDatos.datos
                            .reverse()
                            .map(d => ({
                                hora: new Date(d.fecha_hora).toLocaleTimeString('es-ES', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                }),
                                valor: parseFloat(d.valor),
                                fecha: d.fecha_hora
                            }));

                        datos[sensor.id] = datosFormateados;

                        const valores = datosFormateados.map(d => d.valor);
                        const promedio = valores.reduce((a, b) => a + b, 0) / valores.length;
                        promediosTemp[sensor.id] = promedio.toFixed(2);
                        ultimosTemp[sensor.id] = valores[valores.length - 1].toFixed(2);
                    }
                }

                setDatosAgrupados(datos);
                setPromedios(promediosTemp);
                setUltimosValores(ultimosTemp);
            }
        } catch (error) {
            console.error("Error cargando datos del dashboard:", error);
        } finally {
            setLoading(false);
        }
    };

    const getSensorPorTipo = (tipo) => {
        return sensores.find(s => s.tipo_sensor?.toLowerCase() === tipo.toLowerCase());
    };

    const getUnidad = (tipo) => {
        const unidades = {
            temperatura: "°C",
            humedad: "%",
            ph: "",
            luz: " lux",
            calidad_aire: ""
        };
        return unidades[tipo?.toLowerCase()] || "";
    };

    const verificarAlerta = (sensor) => {
        if (!sensor || !ultimosValores[sensor.id]) return null;

        const valor = parseFloat(ultimosValores[sensor.id]);
        const { valor_minimo, valor_maximo } = sensor;

        if (valor_minimo && valor < valor_minimo) {
            return `Valor bajo (${valor}${getUnidad(sensor.tipo_sensor)})`;
        }
        if (valor_maximo && valor > valor_maximo) {
            return `Valor alto (${valor}${getUnidad(sensor.tipo_sensor)})`;
        }
        return null;
    };

    const handleVerDetalles = (sensorId) => {
        navigate(`/devices/${sensorId}`);
    };

    const sensorTemp = getSensorPorTipo("temperatura");
    const sensorHum = getSensorPorTipo("humedad");
    const sensorPH = getSensorPorTipo("ph");
    const sensorLuz = getSensorPorTipo("luz");
    const sensorAire = getSensorPorTipo("calidad_aire");

    const SensorCard = ({ sensor, tipo, unidad, rango }) => (
        <div className="quesadilla-card quesadilla-dark">
            <h4 className="sushi-label">{tipo.toUpperCase()}</h4>
            {sensor && ultimosValores[sensor.id] ? (
                <>
                    <div className="pho-valor">{ultimosValores[sensor.id]}{unidad}</div>
                    {rango && <div className="kimchi-rango">Rango ideal: {rango}</div>}
                    <div className="bibimbap-promedio">
                        <TrendingUp size={14} /> Promedio: {promedios[sensor.id]}{unidad}
                    </div>
                    <button
                        className="taco-boton taco-boton-mini"
                        onClick={() => handleVerDetalles(sensor.id)}
                    >
                        <Eye size={14} />
                        Ver detalles
                    </button>
                </>
            ) : (
                <div className="empanada-vacio">
                    <p>Sin datos disponibles</p>
                    <div className="kimchi-rango">Sensor: {sensor?.nombre || "No configurado"}</div>
                </div>
            )}
        </div>
    );

    const GraficoCard = ({ sensor, tipo, datos, ChartComponent }) => (
        <div className="quesadilla-card quesadilla-dark">
            <h4 className="sushi-label">Tendencia de {tipo}</h4>
            {sensor && datos && datos.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                    <ChartComponent data={datos}>
                        {ChartComponent === AreaChart && (
                            <defs>
                                <linearGradient id={`color${sensor.id}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={getColorGrafico(sensor.tipo_sensor)} stopOpacity={0.8} />
                                    <stop offset="95%" stopColor={getColorGrafico(sensor.tipo_sensor)} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                        )}
                        <CartesianGrid strokeDasharray="3 3" stroke="#2d3561" />
                        <XAxis dataKey="hora" stroke="#8b92b0" style={{ fontSize: '11px' }} />
                        <YAxis stroke="#8b92b0" style={{ fontSize: '11px' }} />
                        <Tooltip contentStyle={{ background: '#1a1f3a', border: '1px solid #2d3561', borderRadius: '8px' }} />
                        {ChartComponent === LineChart ? (
                            <Line type="monotone" dataKey="valor" stroke={getColorGrafico(sensor.tipo_sensor)} strokeWidth={2} />
                        ) : (
                            <Area type="monotone" dataKey="valor" stroke={getColorGrafico(sensor.tipo_sensor)} fillOpacity={1} fill={`url(#color${sensor.id})`} />
                        )}
                    </ChartComponent>
                </ResponsiveContainer>
            ) : (
                <div className="empanada-vacio">
                    <Activity size={40} style={{ opacity: 0.3, marginBottom: '16px' }} />
                    <p>No hay datos para mostrar</p>
                    <div className="kimchi-rango">
                        {sensor ? "Esperando datos del sensor..." : "Sensor no configurado"}
                    </div>
                </div>
            )}
        </div>
    );

    const getColorGrafico = (tipo) => {
        const colores = {
            temperatura: "#ff7300",
            humedad: "#8884d8",
            ph: "#00c49f",
            luz: "#82ca9d"
        };
        return colores[tipo?.toLowerCase()] || "#8884d8";
    };

    return (
        <div className="burrito-root burrito-oscuro">
            <div className="fajita-sidebar">
                <Sidebar isOpen={isOpen} setIsOpen={setIsOpen} />
            </div>
            <div className="enchilada-main">
                <Header isOpen={isOpen} setIsOpen={setIsOpen} />
                <div className="tamale-content">
                    {loading ? (
                        <div className="ceviche-loading">
                            <Activity size={40} className="tostada-spin" />
                            <p className="arepa-text">Cargando dashboard...</p>
                        </div>
                    ) : (
                        <div className="grid-stack">

                            <div className="grid-stack-item" gs-w="3" gs-h="3" gs-x="0" gs-y="0">
                                <div className="grid-stack-item-content quesadilla-card quesadilla-dark parrilla-estado-sistema">
                                    <h4 className="sushi-label">ESTADO DEL SISTEMA</h4>
                                    <div className="risotto-estado">
                                        <span className="cannoli-dot cannoli-verde"></span>
                                        <span>Operativo</span>
                                    </div>
                                    <div className="baguette-info">
                                        Última actualización: {new Date().toLocaleTimeString('es-ES')}
                                    </div>
                                    <div className="baguette-info" style={{ marginTop: '8px' }}>
                                        <strong>{sensores.length}</strong> sensores configurados<br />
                                        <strong>{sensores.filter(s => s.estado === "activo").length}</strong> sensores activos
                                    </div>
                                    <div className="focaccia-version">v1.0.0 © 2025 AgroNexus</div>
                                </div>
                            </div>

                            <div className="grid-stack-item" gs-w="3" gs-h="3" gs-x="3" gs-y="0">
                                <div className="grid-stack-item-content">
                                    <SensorCard sensor={sensorTemp} tipo="Temperatura" unidad="°C" rango="20-28°C" />
                                </div>
                            </div>

                            <div className="grid-stack-item" gs-w="3" gs-h="3" gs-x="6" gs-y="0">
                                <div className="grid-stack-item-content">
                                    <SensorCard sensor={sensorHum} tipo="Humedad" unidad="%" rango="60-70%" />
                                </div>
                            </div>

                            <div className="grid-stack-item" gs-w="3" gs-h="3" gs-x="9" gs-y="0">
                                <div className="grid-stack-item-content">
                                    <SensorCard sensor={sensorPH} tipo="pH" unidad="" rango="6.0-7.0" />
                                </div>
                            </div>

                            <div className="grid-stack-item" gs-w="3" gs-h="3" gs-x="0" gs-y="3">
                                <div className="grid-stack-item-content">
                                    <SensorCard sensor={sensorLuz} tipo="Luz" unidad=" lux" rango="500-1000 lux" />
                                </div>
                            </div>

                            <div className="grid-stack-item" gs-w="3" gs-h="3" gs-x="3" gs-y="3">
                                <div className="grid-stack-item-content">
                                    <SensorCard sensor={sensorAire} tipo="Calidad del Aire" unidad="" rango="" />
                                </div>
                            </div>

                            <div className="grid-stack-item" gs-w="6" gs-h="4" gs-x="6" gs-y="3">
                                <div className="grid-stack-item-content" style={{ minHeight: '280px' }}>
                                    <GraficoCard
                                        sensor={sensorTemp}
                                        tipo="Temperatura"
                                        datos={sensorTemp ? datosAgrupados[sensorTemp.id] : null}
                                        ChartComponent={LineChart}
                                    />
                                </div>
                            </div>

                            <div className="grid-stack-item" gs-w="6" gs-h="4" gs-x="0" gs-y="6">
                                <div className="grid-stack-item-content" style={{ minHeight: '280px' }}>
                                    <GraficoCard
                                        sensor={sensorHum}
                                        tipo="Humedad"
                                        datos={sensorHum ? datosAgrupados[sensorHum.id] : null}
                                        ChartComponent={AreaChart}
                                    />
                                </div>
                            </div>

                            <div className="grid-stack-item" gs-w="6" gs-h="4" gs-x="6" gs-y="7">
                                <div className="grid-stack-item-content" style={{ minHeight: '280px' }}>
                                    <GraficoCard
                                        sensor={sensorPH}
                                        tipo="pH"
                                        datos={sensorPH ? datosAgrupados[sensorPH.id] : null}
                                        ChartComponent={LineChart}
                                    />
                                </div>
                            </div>
                            <div className="grid-stack-item" gs-w="6" gs-h="4" gs-x="0" gs-y="10">
                                <div className="grid-stack-item-content" style={{ minHeight: '280px' }}>
                                    <GraficoCard
                                        sensor={sensorLuz}
                                        tipo="Luz"
                                        datos={sensorLuz ? datosAgrupados[sensorLuz.id] : null}
                                        ChartComponent={AreaChart}
                                    />
                                </div>
                            </div>

                            <div className="grid-stack-item" gs-w="12" gs-h="4" gs-x="0" gs-y="14">
                                <div className="grid-stack-item-content quesadilla-card quesadilla-dark">
                                    <h4 className="sushi-label">Sensores Conectados</h4>
                                    <div className="lasagna-wrapper">
                                        <table className="lasagna-tabla">
                                            <thead>
                                                <tr>
                                                    <th>Sensor</th>
                                                    <th>Tipo</th>
                                                    <th>Estado</th>
                                                    <th>Última lectura</th>
                                                    <th>Promedio</th>
                                                    <th>Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sensores.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="6" className="moussaka-vacio">
                                                            No hay sensores configurados
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    sensores.map(sensor => (
                                                        <tr key={sensor.id}>
                                                            <td>{sensor.nombre}</td>
                                                            <td>{sensor.tipo_sensor}</td>
                                                            <td>
                                                                <span className={sensor.estado === "activo" ? "croissant-activo" : "croissant-inactivo"}>
                                                                    {sensor.estado || "inactivo"}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                {ultimosValores[sensor.id]
                                                                    ? `${ultimosValores[sensor.id]}${getUnidad(sensor.tipo_sensor)}`
                                                                    : "Sin datos"}
                                                            </td>
                                                            <td>
                                                                {promedios[sensor.id]
                                                                    ? `${promedios[sensor.id]}${getUnidad(sensor.tipo_sensor)}`
                                                                    : "N/A"}
                                                            </td>
                                                            <td>
                                                                <button
                                                                    className="taco-boton taco-boton-mini"
                                                                    onClick={() => handleVerDetalles(sensor.id)}
                                                                >
                                                                    <Eye size={14} />
                                                                    Detalles
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <div className="grid-stack-item" gs-w="6" gs-h="3" gs-x="0" gs-y="18">
                                <div className="grid-stack-item-content quesadilla-card quesadilla-dark">
                                    <h4 className="sushi-label">
                                        <AlertCircle size={18} />
                                        Alertas del Sistema
                                    </h4>
                                    <ul className="tiramisu-lista">
                                        {sensores.length === 0 ? (
                                            <li className="carbonara-item">No hay sensores configurados</li>
                                        ) : sensores.map(sensor => {
                                            const alerta = verificarAlerta(sensor);
                                            if (alerta) {
                                                return (
                                                    <li key={sensor.id} className="carbonara-item carbonara-error">
                                                        <strong>{sensor.nombre}:</strong> {alerta}
                                                    </li>
                                                );
                                            }
                                            return (
                                                <li key={sensor.id} className="carbonara-item carbonara-success">
                                                    <strong>{sensor.nombre}:</strong>
                                                    {ultimosValores[sensor.id]
                                                        ? " Funcionando correctamente"
                                                        : " Sin datos recientes"}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </div>

                            <div className="grid-stack-item" gs-w="6" gs-h="3" gs-x="6" gs-y="18">
                                <div className="grid-stack-item-content quesadilla-card quesadilla-dark">
                                    <h4 className="sushi-label">Resumen General</h4>
                                    <div style={{ fontSize: '0.9rem', lineHeight: '1.8' }}>
                                        <div className="baguette-info">
                                            📊 <strong>Total de sensores:</strong> {sensores.length}
                                        </div>
                                        <div className="baguette-info">
                                            ✅ <strong>Activos:</strong> {sensores.filter(s => s.estado === "activo").length}
                                        </div>
                                        <div className="baguette-info">
                                            ⚠️ <strong>Con alertas:</strong> {sensores.filter(s => verificarAlerta(s)).length}
                                        </div>
                                        <div className="baguette-info">
                                            📡 <strong>Con datos:</strong> {sensores.filter(s => ultimosValores[s.id]).length}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;